import React, { useEffect } from "react";
import { formatDate, renderAmount, parseFixed } from "../../utils";
import PdfGenerator from "../../shared/PdfGenerator";
import ReportSummaryTable from "./ReportSummaryTable";
import MultiEmployeeSummaryTable from "./MultiEmployeeSummaryTable";
import MultiClientSummaryTable from "./MultiClientSummaryTable";
import {
  drawInvoiceMetricSections,
  drawInvoiceSummarySections,
} from "./invoiceAnalyticsPdf";
import { PDF_MARGIN, getPdfTheme } from "../../utils/pdfTheme";
import ReportDataTable from "./ReportDataTable";
import GroupedPayDateSummaryTable from "./GroupedPayDateSummaryTable";

/**
 * ReportLayout - PDF layout for reports following the same pattern as Invoice/Payout/Manifest layouts
 *
 * @param {Object} props
 * @param {Array} props.data - Report data array
 * @param {Object} props.summary - Report summary statistics
 * @param {string} props.reportName - Name of the report
 * @param {Object} props.reportConfig - Report configuration
 * @param {Object} props.reportMetadata - Report metadata (date range, etc.)
 * @param {Object} props.parentCompany - Parent company information
 * @param {Object} [props.analytics] - Invoice analytics from the reports API (invoice reports only)
 */
const ReportLayout = ({
  data = [],
  summary = {},
  reportName = "Report",
  reportConfig = {},
  reportMetadata = {},
  parentCompany = {},
  hasMultipleEmployees = false,
  hasMultipleClients = false,
  analytics = null,
  children,
  ...props
}) => {
  // Move console logs to useEffect to ensure they execute
  useEffect(() => {
    console.log("=== ReportLayout rendered ===");
    console.log("reportConfig", reportConfig);
    console.log("reportMetadata", reportMetadata);
    console.log("parentCompany", parentCompany);
    console.log("data", data);
    console.log("data length:", data?.length);
    console.log("summary", summary);
    console.log("summary keys:", summary ? Object.keys(summary) : "no summary");
    if (summary) {
      console.log("summary.totalSum:", summary.totalSum);
      console.log("summary.total:", summary.total);
      console.log("summary.legs_payoutTotalSum:", summary.legs_payoutTotalSum);
      console.log("summary.legsCount:", summary.legsCount);
    }
    console.log("children", children);
    console.log("props", props);
  }, [
    reportConfig,
    reportMetadata,
    parentCompany,
    data,
    summary,
    children,
    props,
  ]);

  const formatDateRange = () => {
    if (
      !reportMetadata?.dateRange?.startDate ||
      !reportMetadata?.dateRange?.endDate
    ) {
      return "N/A";
    }

    const startDate = new Date(reportMetadata.dateRange.startDate);
    const endDate = new Date(reportMetadata.dateRange.endDate);

    switch (reportMetadata.dateRangeType) {
      case "custom":
        return `${formatDate(startDate, null, true)} - ${formatDate(
          endDate,
          null,
          true
        )}`;
      case "monthly":
        return formatDate(startDate, null, true).split(",")[0]; // Just month and year
      case "quarterly":
        const quarter = Math.ceil((startDate.getMonth() + 1) / 3);
        return `Q${quarter} ${startDate.getFullYear()}`;
      case "yearly":
        return startDate.getFullYear().toString();
      default:
        return `${formatDate(startDate, null, true)} - ${formatDate(
          endDate,
          null,
          true
        )}`;
    }
  };

  function didParseCell({ cell, section, row, ...rest }) {
    cell.styles.fontSize = 8;
    if (section === "head") {
      cell.styles.fontSize = 9;
      cell.styles.fontStyle = "bold";
    }
    if (section === "body") {
      cell.styles.fillColor = [255, 255, 255];
    }
  }

  function didDrawPage({ pageCount, pageNumber, settings, doc, ...rest }) {
    var totalPagesExp = "{total_pages_count_string}";
    var str = "Page " + pageCount;
    if (typeof doc.putTotalPages === "function") {
      str = str + " of " + totalPagesExp;
    }
    doc.setFontSize(10);
    var pageHeight =
      doc.internal.pageSize.height || doc.internal.pageSize.getHeight();
    doc.text(str, settings.margin.left, pageHeight - 5);
  }

  /**
   * Calculate totals from summary or data for overview display
   * Summary fields follow patterns: fieldNameSum, fieldNameCount, fieldNameAvg
   */
  const calculateTotals = () => {
    const modelName = reportConfig?.modelName || "";
    const totals = {};

    // Extract totals from summary object
    if (summary) {
      // For Payout reports
      if (modelName === "Payout" || modelName === "Payouts") {
        // Look for various field name patterns
        totals.totalMileage =
          summary.totalMileageSum ||
          summary.totalMileage ||
          summary.legs_totalMileageSum ||
          summary.legs_mileageSum ||
          0;

        totals.mileageTotal =
          summary.mileageTotalSum ||
          summary.mileageTotal ||
          summary.legs_payoutMileageTotalSum ||
          summary.legs_mileageTotalSum ||
          0;

        totals.supplementalTotal =
          summary.supplementalTotalSum ||
          summary.supplementalTotal ||
          summary.legs_payoutSupplementalSum ||
          summary.legs_supplementalSum ||
          0;

        totals.totalGross =
          summary.totalSum || summary.total || summary.legs_payoutTotalSum || 0;

        totals.totalDrops = summary.legsCount || summary.totalDrops || 0;

        totals.totalHours =
          summary.hoursSum ||
          summary.hours ||
          summary.legs_hoursSum ||
          summary.shifts_totalWorkTimeSum ||
          0;

        totals.hourlyRate = summary.hourlyRateAvg || summary.hourlyRate || 0;
      }
      // For Job reports
      else if (modelName === "Job" || modelName === "Jobs") {
        totals.totalCharges =
          summary.totalSum || summary.total || summary.totalChargesSum || 0;
        totals.baseCharges =
          summary.baseChargeSum ||
          summary.baseCharge ||
          summary.totalBaseChargesSum ||
          0;
        totals.fuelSurcharge =
          summary.fuelSurchargeSum ||
          summary.fuelSurcharge ||
          summary.totalFuelChargesSum ||
          0;
        totals.otherCharges =
          summary.otherChargesSum ||
          summary.otherCharges ||
          summary.totalOtherChargesSum ||
          0;
      }
      // For Invoice reports
      else if (modelName === "Invoice" || modelName === "Invoices") {
        totals.totalAmount =
          summary.totalSum || summary.total || summary.totalAmountSum || 0;
        totals.baseCharges =
          summary.totalBaseChargesSum ||
          summary.totalBaseCharges ||
          summary.baseChargesSum ||
          0;
        totals.fuelCharges =
          summary.totalFuelChargesSum ||
          summary.totalFuelCharges ||
          summary.fuelChargesSum ||
          0;
        totals.otherCharges =
          summary.totalOtherChargesSum ||
          summary.totalOtherCharges ||
          summary.otherChargesSum ||
          0;
        totals.dropCharges =
          summary.totalDropChargesSum ||
          summary.totalDropCharges ||
          summary.dropChargesSum ||
          0;
      }
      // For Manifest reports
      else if (modelName === "Manifest" || modelName === "Manifests") {
        totals.totalMileage =
          summary.totalMileageSum ||
          summary.totalMileage ||
          summary.legs_totalMileageSum ||
          0;
        totals.totalLegs = summary.legsCount || summary.totalLegs || 0;
      }
    }

    return totals;
  };

  function createHeader(pdf) {
    // REPORT TITLE
    pdf.setFontSize(9);
    pdf.setFont("Times New Roman");
    pdf.setTextColor(100, 100, 100);
    pdf.text("REPORT", 105, 5, "center");

    pdf.setFontSize(11);
    pdf.setTextColor("black");
    pdf.text(`${reportName}`, 105, 9, "center");

    // Company NAME
    pdf.setFontSize(20);
    pdf.setFont(undefined, "bold");
    const companyName = parentCompany?.name || "Company";
    pdf.text(companyName, 5, 20);

    // REPORT INFO
    pdf.setFontSize(9);
    pdf.setFont("Times New Roman");
    pdf.setTextColor(100, 100, 100);

    let currentY = 30;

    pdf.text("Report Type: ", 5, currentY);
    pdf.setTextColor("black");
    pdf.text(`${reportConfig?.modelName || "N/A"}`, 30, currentY);
    currentY += 5;

    pdf.setTextColor(100, 100, 100);
    pdf.text("Date Range: ", 5, currentY);
    pdf.setTextColor("black");
    pdf.text(formatDateRange(), 30, currentY);
    currentY += 5;

    pdf.setTextColor(100, 100, 100);
    pdf.text("Generated: ", 5, currentY);
    pdf.setTextColor("black");
    pdf.text(formatDate(new Date(), null, true), 30, currentY);
    currentY += 5;

    // TOTALS/OVERVIEW SECTION
    const totals = calculateTotals();
    const modelName = reportConfig?.modelName || "";
    const hasTotals = Object.keys(totals).length > 0;

    if (hasTotals) {
      // Add spacing before totals
      currentY += 3;

      // For Payout reports - show totals similar to PayoutLayout
      if (modelName === "Payout" || modelName === "Payouts") {
        if (totals.totalMileage) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Total Miles: ", 125, currentY - 10);
          pdf.setTextColor("black");
          pdf.text(
            totals.totalMileage.toLocaleString("en-US"),
            150,
            currentY - 10
          );
        }

        // if (totals.totalDrops) {
        //   pdf.setTextColor(100, 100, 100);
        //   pdf.text("Total Drops: ", 87, currentY);
        //   pdf.setTextColor("black");
        //   pdf.text(totals.totalDrops.toString(), 110, currentY);
        // }

        if (totals.hourlyRate && totals.totalHours) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Standby Rate: ", 55, currentY + 5);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.hourlyRate), 75, currentY + 5);
          pdf.setTextColor(100, 100, 100);
          pdf.text("Standby Hours: ", 100, currentY + 5);
          pdf.setTextColor("black");
          pdf.text(parseFixed(totals.totalHours).toString(), 125, currentY + 5);
        }

        if (totals.mileageTotal) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Mileage Total: ", 125, currentY);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.mileageTotal), 150, currentY);
        }

        if (totals.supplementalTotal !== undefined) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Supplemental Total: ", 125, currentY - 5);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.supplementalTotal), 155, currentY - 5);
        }

        if (totals.totalGross) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Total Gross: ", 125, currentY + 5);
          pdf.setTextColor("black");
          pdf.setFont(undefined, "bold");
          pdf.text(renderAmount(totals.totalGross), 150, currentY + 5);
          pdf.setFont(undefined, "normal");
        }
      }
      // For Job reports
      else if (modelName === "Job" || modelName === "Jobs") {
        if (totals.baseCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Base Charges: ", 5, currentY);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.baseCharges), 35, currentY);
        }

        if (totals.fuelSurcharge) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Fuel Surcharge: ", 5, currentY + 5);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.fuelSurcharge), 35, currentY + 5);
        }

        if (totals.otherCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Other Charges: ", 125, currentY);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.otherCharges), 150, currentY);
        }

        if (totals.totalCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Total Charges: ", 125, currentY + 5);
          pdf.setTextColor("black");
          pdf.setFont(undefined, "bold");
          pdf.text(renderAmount(totals.totalCharges), 150, currentY + 5);
          pdf.setFont(undefined, "normal");
        }
      }
      // For Invoice reports
      else if (modelName === "Invoice" || modelName === "Invoices") {
        if (totals.baseCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Base Charges: ", 5, currentY);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.baseCharges), 35, currentY);
        }

        if (totals.fuelCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Fuel Charges: ", 5, currentY + 5);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.fuelCharges), 35, currentY + 5);
        }

        if (totals.dropCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Drop Charges: ", 5, currentY + 10);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.dropCharges), 35, currentY + 10);
        }

        if (totals.otherCharges) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Other Charges: ", 125, currentY);
          pdf.setTextColor("black");
          pdf.text(renderAmount(totals.otherCharges), 150, currentY);
        }

        if (totals.totalAmount) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Total Amount: ", 125, currentY + 5);
          pdf.setTextColor("black");
          pdf.setFont(undefined, "bold");
          pdf.text(renderAmount(totals.totalAmount), 150, currentY + 5);
          pdf.setFont(undefined, "normal");
        }
      }
      // For Manifest reports
      else if (modelName === "Manifest" || modelName === "Manifests") {
        if (totals.totalMileage) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Total Mileage: ", 5, currentY);
          pdf.setTextColor("black");
          pdf.text(totals.totalMileage.toLocaleString("en-US"), 35, currentY);
        }

        if (totals.totalLegs) {
          pdf.setTextColor(100, 100, 100);
          pdf.text("Total Legs: ", 125, currentY);
          pdf.setTextColor("black");
          pdf.text(totals.totalLegs.toString(), 150, currentY);
        }
      }
    }

    // Records count on the right side
    pdf.setTextColor(100, 100, 100);
    pdf.text("Records: ", 125, 30);
    pdf.setTextColor("black");
    pdf.text(`${summary?.totalRecords || data.length}`, 140, 30);
  }

  function willDrawCell({ cell, ...props }) {
    cell.styles.fontSize = 8;
  }

  // Check if data is grouped by payDate
  const isGroupedByPayDate = React.useMemo(() => {
    if (!data || data.length === 0) return false;
    const modelName = reportConfig?.modelName || "";
    return (
      (modelName === "Payout" || modelName === "Payouts") &&
      data.some((item) => item._groupPayDate || item._groupPayDateString)
    );
  }, [data, reportConfig]);

  const modelName = reportConfig?.modelName || "";
  const isInvoiceReport = modelName === "Invoice" || modelName === "Invoices";
  const hasInvoiceAnalytics = isInvoiceReport && !!analytics?.summary;

  // Themed, programmatic invoice sections: summary before the rows, metrics after
  const invoicePdfProps = React.useMemo(() => {
    if (!hasInvoiceAnalytics) return {};
    const theme = getPdfTheme();
    return {
      drawBeforeData: (pdf, startY) =>
        drawInvoiceSummarySections(pdf, analytics, startY, theme, {
          invoiceCount: data?.length || 0,
        }),
      drawAfterData: (pdf, startY) =>
        drawInvoiceMetricSections(pdf, analytics, startY, theme),
      dataTableOptions: {
        useCss: false,
        theme: "grid",
        margin: PDF_MARGIN,
        styles: {
          font: "helvetica",
          fontSize: 7.5,
          textColor: theme.text,
          lineColor: theme.border,
          lineWidth: 0.1,
          cellPadding: 1.4,
        },
        headStyles: {
          fillColor: theme.primary,
          textColor: theme.white,
          fontStyle: "bold",
          fontSize: 7.5,
        },
        alternateRowStyles: { fillColor: theme.zebra },
        columnStyles: Object.fromEntries(
          [5, 6, 7, 8, 9].map((index) => [index, { halign: "right" }])
        ),
      },
    };
  }, [hasInvoiceAnalytics, analytics, data]);

  const layout = (
    <>
      {/* Without analytics (older responses) fall back to the HTML per-client table */}
      {isInvoiceReport && !hasInvoiceAnalytics && hasMultipleClients && data && data.length > 0 && (
        <MultiClientSummaryTable data={data} />
      )}
      {hasMultipleEmployees ? (
        // Multi-employee layout: show per-employee totals
        <>
          {data && data.length > 0 && (
            <MultiEmployeeSummaryTable data={data} reportConfig={reportConfig} />
          )}
          {data && data.length > 0 && (
            <ReportDataTable data={data} reportConfig={reportConfig} />
          )}
          {isGroupedByPayDate && data && data.length > 0 && (
            <GroupedPayDateSummaryTable data={data} reportConfig={reportConfig} />
          )}
        </>
      ) : (
        // Single employee layout: show regular summary
        <>
          {summary &&
            (summary.totals?.length > 0 || summary.averages?.length > 0) && (
              <ReportSummaryTable summary={summary} reportConfig={reportConfig} />
            )}
          {data && data.length > 0 && (
            <ReportDataTable data={data} reportConfig={reportConfig} />
          )}
          {isGroupedByPayDate && data && data.length > 0 && (
            <GroupedPayDateSummaryTable data={data} reportConfig={reportConfig} />
          )}
        </>
      )}
    </>
  );

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { ...props });
    }
    return child;
  });

  const pageData = `|  ${parentCompany?.name || "Company"}  |  ${reportName}`;
  const pdfFileName = `${reportName
    .toLowerCase()
    .replace(/\s+/g, "-")}-${formatDate(new Date(), null, true)}.pdf`;

  // Calculate startY based on whether we have totals to display
  const totals = calculateTotals();
  const hasTotals = Object.keys(totals).length > 0;
  const startY = hasTotals ? 70 : 50; // More space if totals are displayed

  return (
    <PdfGenerator
      startY={startY}
      didDrawPage={didDrawPage}
      didParseCell={didParseCell}
      createHeader={createHeader}
      willDrawCell={willDrawCell}
      fileName={pdfFileName}
      id="report-data-table"
      rowPageBreak="avoid"
      pageData={pageData}
      layout={layout}
      columnStyles={{ text: { cellWidth: "auto" } }}
      {...invoicePdfProps}
    >
      <Layout download={props.download} onComplete={props.onComplete}>
        {childrenWithProps}
      </Layout>
    </PdfGenerator>
  );
};

const Layout = (props) => {
  // Trigger PDF download when download prop is true
  useEffect(() => {
    if (props.download && props.handleDownloadPdf) {
      // Wait for React to render the tables in the hidden div
      // Give enough time for the tables to be rendered
      const timeoutId = setTimeout(() => {
        try {
          // Call handleDownloadPdf which will save the PDF
          // It returns a promise that resolves when the PDF is saved
          const pdfPromise = props.handleDownloadPdf();
          if (pdfPromise && typeof pdfPromise.then === "function") {
            pdfPromise
              .then(() => {
                // PDF download completed
                if (props.onComplete) {
                  props.onComplete();
                }
              })
              .catch((error) => {
                console.error("Error generating PDF:", error);
                if (props.onComplete) {
                  props.onComplete();
                }
              });
          } else {
            // If it doesn't return a promise, assume it completed
            setTimeout(() => {
              if (props.onComplete) {
                props.onComplete();
              }
            }, 500);
          }
        } catch (error) {
          console.error("Error calling handleDownloadPdf:", error);
          if (props.onComplete) {
            props.onComplete();
          }
        }
      }, 1500); // Wait 1.5 seconds for tables to render

      return () => clearTimeout(timeoutId);
    }
  }, [props.download, props.handleDownloadPdf, props.onComplete]);

  const childrenWithProps = React.Children.map(props.children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { ...props });
    }
    return child;
  });
  return childrenWithProps;
};

export default ReportLayout;
